/**
 * Resize  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo, useRef } from "react";
import { ResizeContext } from "./ResizeContext";
import {
	IResizeContext,
	IResizeElementData,
	IResizeEventData,
	RESIZE_COMPONENT_TYPE,
	RESIZE_DIRECTION,
	ResizeElementId,
	ResizeEvent
} from "./ResizeTypes";
import { isFunction } from "@adapptio/lib-shared";
import { getMouseOrTouchEventData } from "@adapptio/lib-components";

export interface IResizeProps {
	direction: RESIZE_DIRECTION;
}

const DEFAULT_MAX_FLEX = 100;

const getOffset = (
	event: MouseEvent | Touch,
	handleEl: HTMLElement,
	direction: RESIZE_DIRECTION,
	previousCursorPos: number
) => {
	const { top, bottom, left, right } = handleEl.getBoundingClientRect();

	switch (direction) {
		case RESIZE_DIRECTION.VERTICAL: {
			const offset = event.clientY - top;
			if (offset > 0) {
				if (event.clientY >= top) {
					return offset;
				}
			} else {
				if (event.clientY <= bottom) {
					return offset;
				}
			}
			break;
		}
		case RESIZE_DIRECTION.HORIZONTAL:
		default:
			{
				const offset = event.clientX - previousCursorPos;
				if (offset > 0) {
					if (event.clientX > left) {
						return offset;
					}
				} else {
					if (event.clientX < right) {
						return offset;
					}
				}
			}
			break;
	}
	return 0;
};

const parseSizeString = (size: string): number => {
	return size?.endsWith("px") ? parseInt(size) : 0;
};

export const Resize: FC<IResizeProps> = ({ children, direction }) => {
	const previousCursorPos = useRef<number>();
	const previousHandlePos = useRef<number>();
	const previousOffset = useRef<number>();
	const calculatedValues = useRef<{ pxToFlexValue: number; maxSizes: number[] }>({
		pxToFlexValue: 1,
		maxSizes: []
	});

	/* Resize components data - Child <-> Parent communication */
	const componentsData = useRef<Array<{ id: ResizeElementId } & IResizeElementData>>([]);

	const emitDataUpdate = useCallback(() => {
		const updateData = componentsData.current.map(({ id, resizeDisabled }) => ({ id, resizeDisabled }));

		componentsData.current.forEach((componentData) => {
			if (isFunction(componentData.onDataUpdate)) {
				componentData.onDataUpdate(updateData);
			}
		});
	}, []);

	const updateElement = useCallback(
		(elementId: ResizeElementId, data: Partial<IResizeElementData> | IResizeElementData) => {
			const dataIndex = componentsData.current.findIndex(
				(componentData) => componentData.id === elementId
			);

			if (dataIndex > -1) {
				componentsData.current[dataIndex] = {
					...componentsData.current[dataIndex],
					...data
				};
			} else {
				componentsData.current.push({
					id: elementId,
					...(data as IResizeElementData)
				});
			}

			// Iterate all data to properly set handles
			let disableResize = false;

			for (let i = 0; i < componentsData.current.length; i++) {
				const dataItem = componentsData.current[i];

				if (dataItem.type === RESIZE_COMPONENT_TYPE.ELEMENT) {
					disableResize = dataItem.resizeDisabled;

					if (disableResize) {
						// Go back to set previous handles
						let prevItemIndex = i - 1;

						while (
							componentsData.current[prevItemIndex]?.type === RESIZE_COMPONENT_TYPE.HANDLE ||
							componentsData.current[prevItemIndex]?.registered === false
						) {
							if (
								componentsData.current[prevItemIndex]?.type === RESIZE_COMPONENT_TYPE.HANDLE
							) {
								componentsData.current[prevItemIndex] = {
									...componentsData.current[prevItemIndex],
									resizeDisabled: disableResize
								};
							}

							prevItemIndex = prevItemIndex - 1;
						}
					}

					continue;
				}

				if (dataItem.type === RESIZE_COMPONENT_TYPE.HANDLE) {
					componentsData.current[i] = {
						...componentsData.current[i],
						resizeDisabled: disableResize
					};
				}
			}

			emitDataUpdate();
		},
		[]
	);

	const registerElement = useCallback<IResizeContext["registerElement"]>(
		(elementId, data) => {
			updateElement(elementId, {
				registered: true,
				...data
			});
		},
		[ emitDataUpdate ]
	);

	const unregisterElement = useCallback<IResizeContext["unregisterElement"]>(
		(elementId) => {
			updateElement(elementId, {
				registered: false
			});
		},
		[ emitDataUpdate ]
	);

	/* Helpers */
	const resizeDirection = useMemo<IResizeEventData["direction"]>(
		() => ({
			horizontal: direction === RESIZE_DIRECTION.HORIZONTAL,
			vertical: direction === RESIZE_DIRECTION.VERTICAL
		}),
		[ direction ]
	);

	const getElSizeInPx = useCallback(
		(element: HTMLElement) =>
			({
				[RESIZE_DIRECTION.HORIZONTAL]: element.offsetWidth,
				[RESIZE_DIRECTION.VERTICAL]: element.offsetHeight
			}[direction]),
		[ direction ]
	);

	const getElPosition = useCallback(
		(element: HTMLElement) =>
			({
				[RESIZE_DIRECTION.HORIZONTAL]: element.getBoundingClientRect().left,
				[RESIZE_DIRECTION.VERTICAL]: element.getBoundingClientRect().top
			}[direction]),
		[ direction ]
	);

	const getCursorPosition = useCallback(
		(event: ResizeEvent) => {
			const eventData = getMouseOrTouchEventData(event);

			return {
				[RESIZE_DIRECTION.HORIZONTAL]: eventData.clientX,
				[RESIZE_DIRECTION.VERTICAL]: eventData.clientY
			}[direction];
		},
		[ direction ]
	);

	const getPxToFlexValue = useCallback(() => {
		const elementsData = componentsData.current?.filter(
			(data) => data.registered && data.type === RESIZE_COMPONENT_TYPE.ELEMENT
		);
		const componentWithFlexSize = elementsData.find((data) => Number.isFinite(data.size));
		if (componentWithFlexSize) {
			return (componentWithFlexSize.size as number) / getElSizeInPx(componentWithFlexSize.ref?.current);
		}

		const sumOfSizes = elementsData.reduce((acc, item) => acc + getElSizeInPx(item?.ref?.current), 0);
		return DEFAULT_MAX_FLEX / sumOfSizes;
	}, [ componentsData.current, getElSizeInPx ]);

	const getMinPxSize = useCallback(
		(element: HTMLElement) => {
			const style = window.getComputedStyle(element);

			switch (direction) {
				case RESIZE_DIRECTION.HORIZONTAL:
					return parseSizeString(style.getPropertyValue("min-width"));
				case RESIZE_DIRECTION.VERTICAL:
					return parseSizeString(style.getPropertyValue("min-height"));
			}
		},
		[ direction ]
	);

	const getResizedElementsData = useCallback(
		(splitterId: ResizeElementId): [IResizeElementData, IResizeElementData] => {
			const handleIndex = componentsData.current.findIndex(
				(componentData) => componentData.id === splitterId
			);

			if (handleIndex < 0) {
				throw new Error(`Splitter id ${splitterId} was not found in elementsIds`);
			}

			let prevDataIndex = handleIndex - 1;
			let prevElementData = null;

			while (componentsData.current[prevDataIndex] && !prevElementData) {
				if (
					componentsData.current[prevDataIndex]?.type === RESIZE_COMPONENT_TYPE.ELEMENT &&
					componentsData.current[prevDataIndex].registered
				) {
					prevElementData = componentsData.current[prevDataIndex];
				}
				prevDataIndex = prevDataIndex - 1;
			}

			let nextDataIndex = handleIndex + 1;
			let nextElementData = null;

			while (componentsData.current[nextDataIndex] && !nextElementData) {
				if (
					componentsData.current[nextDataIndex]?.type === RESIZE_COMPONENT_TYPE.ELEMENT &&
					componentsData.current[nextDataIndex].registered
				) {
					nextElementData = componentsData.current[nextDataIndex];
				}
				nextDataIndex = nextDataIndex + 1;
			}

			return [ prevElementData, nextElementData ];
		},
		[]
	);

	/* Resizing handlers */
	const onStartResize = useCallback<IResizeContext["onStartResize"]>(
		(splitterId, event, handleEl) => {
			const resizedElementsData = getResizedElementsData(splitterId);

			previousCursorPos.current = getCursorPosition(event);
			previousHandlePos.current = getElPosition(handleEl);
			previousOffset.current = 0;

			const pxToFlexValue = getPxToFlexValue();

			const maxSumSize = resizedElementsData.reduce(
				(acc, data) => acc + getElSizeInPx(data.ref.current),
				0
			);

			const maxSizes = [
				maxSumSize - getMinPxSize(resizedElementsData[1].ref.current),
				maxSumSize - getMinPxSize(resizedElementsData[0].ref.current)
			];

			calculatedValues.current = {
				pxToFlexValue,
				maxSizes
			};

			const data: IResizeEventData = {
				offset: 0,
				direction: resizeDirection,
				resizing: true,
				resizedElementsIds: resizedElementsData.map((data) => data.id)
			};

			resizedElementsData.forEach((resElData) => {
				if (isFunction(resElData.onResizeStart)) {
					resElData.onResizeStart(data);
				}
			});
		},
		[ resizeDirection, getPxToFlexValue, getElPosition ]
	);

	const onResize = useCallback<IResizeContext["onResize"]>((splitterId, event, handleEl) => {
		const resizedElementsData = getResizedElementsData(splitterId);

		const eventData = getMouseOrTouchEventData(event);

		const handlePos = getElPosition(handleEl);

		const limitReached =
			previousOffset.current === 0
				? handlePos !== previousHandlePos.current
				: handlePos !== previousHandlePos.current + previousOffset.current;

		const offset = limitReached
			? 0
			: getOffset(eventData as MouseEvent | Touch, handleEl, direction, previousCursorPos.current);

		const commonData: Omit<IResizeEventData, "offset"> = {
			resizing: true,
			direction: resizeDirection,
			resizedElementsIds: resizedElementsData.map((data) => data.id)
		};

		previousHandlePos.current = handlePos;

		const newSizes = resizedElementsData.map((data, index) => {
			const sizeInPx = getElSizeInPx(data.ref.current);
			const minSizeInPx = getMinPxSize(data.ref.current);
			const elOffset = index === 0 ? offset : offset * -1;

			const newSizeInPx = Math.min(
				Math.max(sizeInPx + elOffset, minSizeInPx),
				calculatedValues.current.maxSizes[index]
			);

			if (sizeInPx === minSizeInPx && newSizeInPx === minSizeInPx) {
				return null;
			}

			return newSizeInPx * calculatedValues.current.pxToFlexValue;
		});

		const minSizeReached = newSizes.some((size) => size === null);

		if (!limitReached && !minSizeReached) {
			if (isFunction(resizedElementsData[0].onResize)) {
				resizedElementsData[0].onResize({ offset: offset, newSize: newSizes[0], ...commonData });
			}
			if (isFunction(resizedElementsData[1].onResize)) {
				resizedElementsData[1].onResize({ offset: -offset, newSize: newSizes[1], ...commonData });
			}
		}

		previousCursorPos.current =
			direction === RESIZE_DIRECTION.VERTICAL ? eventData.clientY : eventData.clientX;
		previousOffset.current = offset;
	}, []);

	const onStopResize = useCallback<IResizeContext["onStopResize"]>((splitterId, event, handleEl) => {
		const [ prevResizedElData, nextResizedElData ] = getResizedElementsData(splitterId);

		const eventData = getMouseOrTouchEventData(event);

		const offset = getOffset(
			eventData as MouseEvent | Touch,
			handleEl,
			direction,
			previousCursorPos.current
		);

		const commonData: Omit<IResizeEventData, "offset"> = {
			resizing: false,
			direction: resizeDirection,
			resizedElementsIds: [ prevResizedElData.id, nextResizedElData.id ]
		};

		if (isFunction(prevResizedElData.onResizeStop)) {
			prevResizedElData.onResizeStop({ offset: offset, ...commonData });
		}
		if (isFunction(nextResizedElData.onResizeStop)) {
			nextResizedElData.onResizeStop({ offset: -offset, ...commonData });
		}
	}, []);

	return (
		<ResizeContext.Provider
			value={{ onStartResize, onResize, onStopResize, registerElement, unregisterElement }}
		>
			{children}
		</ResizeContext.Provider>
	);
};
